import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import projectService, { Project } from '../../services/projectService';

interface ImportProjectsDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (projects: Project[]) => void;
}

const ImportProjectsDialog: React.FC<ImportProjectsDialogProps> = ({
  open,
  onClose,
  onImport
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [importedProjects, setImportedProjects] = useState<Project[]>([]);

  const handleImport = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setImportedProjects([]);

      // Importuj projekty z Google Drive
      const projects = await projectService.importProjectsFromDrive();
      
      setImportedProjects(projects);
      setSuccess(`Zaimportowano ${projects.length} projektów`);
      onImport(projects);
    } catch (err: any) {
      setError(err.message || 'Wystąpił błąd podczas importowania projektów');
      console.error('Błąd podczas importowania projektów:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#004273', color: 'white' }}>
        Importuj projekty z Google Drive
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Ta funkcja importuje projekty z Google Drive do bazy danych.
          Projekty są importowane z dysku współdzielonego:
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText 
              primary="Projekty w realizacji" 
              secondary="https://drive.google.com/drive/folders/0AFavSTLoT0DKUk9PVA"
            />
          </ListItem>
        </List>
        
        <Typography variant="body2" sx={{ mt: 2 }}>
          Projekty są identyfikowane na podstawie nazwy folderu, która powinna mieć format "XXX-XXX Nazwa projektu".
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
        
        {importedProjects.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Zaimportowane projekty:
            </Typography>
            <List>
              {importedProjects.map((project, index) => (
                <React.Fragment key={project.project_id}>
                  <ListItem>
                    <ListItemText
                      primary={`${project.project_id}: ${project.project_name}`}
                      secondary={`Link: ${project.google_drive_link || 'Brak'}`}
                    />
                  </ListItem>
                  {index < importedProjects.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          variant="outlined" 
          sx={{ mr: 1 }}
          disabled={loading}
        >
          Zamknij
        </Button>
        <Button 
          onClick={handleImport} 
          variant="contained" 
          disabled={loading}
          sx={{ 
            backgroundColor: '#004273',
            '&:hover': {
              backgroundColor: '#003660'
            }
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Importuj projekty'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportProjectsDialog;
